<template>
  <div v-if="!loading" class="note section" id="note" @click="showSubInfo.value = false">
    <div class="layout-content">
      <SubTitle :text="title" :subText="subText" />

      <div class="region-switch" role="tablist" :aria-label="isEnLang ? 'Challenge region' : '赛题区域切换'">
        <button
          v-for="region in regions"
          :key="region.value"
          class="region-option"
          :class="{ active: activeRegion === region.value }"
          role="tab"
          :aria-selected="activeRegion === region.value"
          @click.stop="activeRegion = region.value"
        >
          <strong>{{ region.title }}</strong>
          <small>{{ region.subtitle }}</small>
        </button>
      </div>

      <transition-group
        name="track-panel"
        tag="div"
        class="l-content"
        :class="`is-${activeRegion}`"
      >
        <div
          class="block"
          :class="`block--${entry.item.type}`"
          v-for="entry in visibleData"
          :key="entry.sourceIndex"
        >
          <div class="title">
            <div class="word">
              <div class="track-identity">
                <span class="track-index">{{ String(entry.displayIndex + 1).padStart(2, '0') }}</span>
                <span class="track-name">{{ entry.item.title }}</span>
                <img class="word-img" :src="entry.item.logo" alt="">
              </div>

              <div
                class="word"
                :class="{
                  'is-origin': entry.item.type === 'ai',
                  'is-purple': entry.item.type === 'blockchain',
                  'is-blue': entry.item.type === 'product'
                }"
              >
                <p class="intro" style="padding-bottom: 0;" v-html="entry.item.subtitle"></p>
                
                <template v-if="entry.item.moreBtn.tabText && entry.item.moreBtn.tabText.length > 0 && entry.item.moreBtn.tabInfoArr">
                  <div class="tab-box">
                    <button v-for="(tabItem, index) in entry.item.moreBtn.tabText" :key="index" @click="changeTab(entry.sourceIndex, index)" class="btn-tab" :class="{'active': tabActiveArr[entry.sourceIndex] === index}">{{ tabItem }}</button>
                  </div>
                  <p class="intro-title mt24">{{entry.item.moreBtn.tabInfoArr[tabActiveArr[entry.sourceIndex]] && entry.item.moreBtn.tabInfoArr[tabActiveArr[entry.sourceIndex]].introTitle}}</p>
                </template>
                <template v-else>
                  <p class="intro-title mt24">{{entry.item.introTitle}}</p>
                </template>
              </div>
            </div>
          </div>

          <template  v-if="entry.item.moreBtn.tabText && entry.item.moreBtn.tabText.length > 0 && entry.item.moreBtn.tabInfoArr">
            <div class="intro" v-html="entry.item.moreBtn.tabInfoArr[tabActiveArr[entry.sourceIndex]] && entry.item.moreBtn.tabInfoArr[tabActiveArr[entry.sourceIndex]].intro"></div>
            <div
              v-if="entry.item.moreBtn.tabInfoArr[tabActiveArr[entry.sourceIndex]] && entry.item.moreBtn.tabInfoArr[tabActiveArr[entry.sourceIndex]].dataDownload"
              class="data-download"
            >
              <a
                class="data-download-link"
                :href="entry.item.moreBtn.tabInfoArr[tabActiveArr[entry.sourceIndex]].dataDownload.url"
                target="_blank"
                rel="noopener noreferrer"
                :aria-label="entry.item.moreBtn.tabInfoArr[tabActiveArr[entry.sourceIndex]].dataDownload.ariaLabel"
                @click.stop
              >
                <span class="data-download-copy">
                  <strong>{{ entry.item.moreBtn.tabInfoArr[tabActiveArr[entry.sourceIndex]].dataDownload.label }}</strong>
                  <small>{{ entry.item.moreBtn.tabInfoArr[tabActiveArr[entry.sourceIndex]].dataDownload.meta }}</small>
                </span>
                <svg class="data-download-icon" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M14 5h5v5M19 5l-8 8M19 13v5a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" />
                </svg>
              </a>
            </div>
          </template>

          <div v-else class="intro" v-html="entry.item.intro"></div>

          <div v-if="entry.item.scenarios" class="ai-requirements">
            <div class="ai-requirements-head">
              <span>{{ isEnLang ? 'Challenge requirements' : '赛题要求' }}</span>
              <strong>{{ isEnLang ? 'Cover at least 3 core scenarios' : '至少覆盖 3 个核心场景，鼓励完整覆盖' }}</strong>
            </div>
            <div class="ai-scenarios">
              <button
                v-for="scenario in entry.item.scenarios"
                :key="scenario.index"
                type="button"
                class="ai-scenario"
                :class="{ 'is-selected': selectedScenarios.includes(scenario.index) }"
                :aria-pressed="selectedScenarios.includes(scenario.index)"
                @click.stop="selectScenario(scenario.index)"
              >
                <span class="ai-scenario-index">{{ scenario.index }}</span>
                <div>
                  <strong>{{ scenario.title }}</strong>
                  <p>{{ scenario.text }}</p>
                </div>
              </button>
            </div>
          </div>

          <div v-if="entry.item.directions" class="international-directions">
            <div class="ai-requirements-head">
              <span>{{ isEnLang ? 'Direction selection' : '方向选择' }}</span>
              <strong>{{ isEnLang ? 'A–E new / extended · F original Agent' : 'A–E 新增 / 扩展 · F 原 Agent' }}</strong>
            </div>
            <div class="direction-list">
              <button
                v-for="direction in entry.item.directions"
                :key="direction.index"
                type="button"
                class="direction-card"
                :class="{ 'is-selected': selectedScenarios.includes(direction.index) }"
                :aria-pressed="selectedScenarios.includes(direction.index)"
                @click.stop="selectScenario(direction.index)"
              >
                <span class="direction-index">{{ direction.index }}</span>
                <div>
                  <strong>{{ direction.title }}</strong>
                  <p>{{ direction.text }}</p>
                </div>
              </button>
            </div>
          </div>

          <div class="block-btn">
            <template  v-if="entry.item.moreBtn.tabText && entry.item.moreBtn.tabText.length > 0 && entry.item.moreBtn.tabInfoArr">
              <div class="block-btn-default"  @click="openSubjectDetail((entry.item.moreBtn.tabInfoArr[tabActiveArr[entry.sourceIndex]] || {}).subjectKey, tabActiveArr[entry.sourceIndex])">
                {{ isEnLang ? 'View Full Challenge' : '查看完整赛题' }}
              </div>
            </template>
            <template v-else>
              <div class="block-btn-default" v-if="entry.item.subjectDetailKey" @click="openSubjectDetail(entry.item.subjectDetailKey)">
                {{ isEnLang ? 'Challenge Brief' : entry.item.moreBtn.details }}
              </div>
              <div  class="block-btn-default" v-else-if="entry.item.moreBtn.detailsText" @click="playText(entry.item.moreBtn.detailsText)">
                {{entry.item.moreBtn.details}}
              </div>
            </template>
            <!-- <a class="block-btn-link" :href="item.moreBtn.understandMoreLink" target="_blank">
              <div class="block-btn-default">
                {{item.moreBtn.understandMore}}
              </div>
            </a> -->
          </div>
        </div>
      </transition-group>
    </div>
  </div>
</template>

<script>
import SubTitle from '@/components/SubTitle'

import * as GlobalLayer from '@/utils/globalLayer'
import { mapState } from 'vuex'
import subjectDetails from '@/data/subjectDetails'
import subjectDetailsEn from '@/data/subjectDetailsEn'
import subjectDetailsAi from '@/data/subjectDetailsAi'
import subjectDetailsAiEn from '@/data/subjectDetailsAiEn'

export default {
  data: () => ({
    showSubInfo: {
      type: 'ai',
      value: false
    },
    qItemWidth: 486,
    tabActiveArr: [],
    selectedScenarios: [],
    loading: true,
    activeRegion: 'domestic'
  }),

  components: {
    SubTitle
  },

  props: ['data'],

  computed: {
    ...mapState([
      'isMobile',
      'isEnLang'
    ]),
    title () {
      return this.isEnLang ? 'SUBJECTS' : '赛题说明'
    },
    subText () {
      return this.isEnLang ? 'FINTECHATHON' : 'SUBJECTS'
    },
    regions () {
      return this.isEnLang
        ? [
            { value: 'domestic', index: '01', title: 'Domestic Tracks', subtitle: 'AI & DATA ANALYTICS' },
            { value: 'international', index: '02', title: 'International Track', subtitle: 'GLOBAL AI' }
          ]
        : [
            { value: 'domestic', index: '01', title: '国内赛道', subtitle: '人工智能 · 数据分析' },
            { value: 'international', index: '02', title: '国际赛道', subtitle: '人工智能' }
          ]
    },
    visibleData () {
      return this.data
        .map((item, sourceIndex) => ({ item, sourceIndex }))
        .filter(({ item, sourceIndex }) => {
          const region = item.region || (sourceIndex === 2 ? 'international' : 'domestic')
          return region === this.activeRegion
        })
        .map(entry => ({ ...entry, displayIndex: entry.sourceIndex }))
    }
  },

  mounted() {
    for (let index = 0; index < this.data.length; index++) {
      const item = this.data[index];
      if (item.moreBtn && item.moreBtn.tabInfoArr && item.moreBtn.tabInfoArr.length > 0) {
        this.tabActiveArr[index] = 0
      } else {
        this.tabActiveArr[index] = -1
      }
    }
    this.loading = false
  },

  methods: {
    selectScenario (index) {
      this.selectedScenarios = this.selectedScenarios.includes(index)
        ? this.selectedScenarios.filter(item => item !== index)
        : [...this.selectedScenarios, index]
    },
    playText (detailsText) {
      GlobalLayer.createModal(this.$store, {
        detailsText,
        isShowBtn: true
      })
    },
    openSubjectDetail (subjectKey, tabIndex) {
      const details = subjectKey === 'aiDomestic' || subjectKey === 'aiDomesticEn'
        ? (this.isEnLang ? subjectDetailsAiEn : subjectDetailsAi)
        : (this.isEnLang ? subjectDetailsEn : subjectDetails)
      const rawDetail = details[subjectKey || `q${Number(tabIndex) + 1}`]
      const subjectDetail = rawDetail && rawDetail.trackLabel
        ? rawDetail
        : rawDetail && {
          ...rawDetail,
          trackLabel: this.isEnLang ? 'DATA ANALYTICS TRACK · CHALLENGE BRIEF' : '数据分析赛道 · 赛题解析'
        }
      if (!subjectDetail) return
      GlobalLayer.createModal(this.$store, { isSubjectDetail: true, subjectDetail })
    },
    changeTab(idx, index) {
      this.tabActiveArr[idx] = index
      this.$forceUpdate()
    },
  }
}
</script>

<style lang="scss" scoped>
.pb0{
  padding-bottom: 0;
}
img {
  width: 100%; height: 100%;
}

.note {
  --track-blue: #315fd8;
  --track-pink: #dc3e73;
  --track-cyan: #078faa;
  position: relative;
  padding: 84px 0 104px;
  background: linear-gradient(180deg, #f7f9fc 0%, #f3f6fa 100%);
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    right: -180px;
    top: 210px;
    width: 420px;
    height: 420px;
    border: 1px solid rgba(49,95,216,.06);
    border-radius: 50%;
    box-shadow: 0 0 0 42px rgba(220,62,115,.025), 0 0 0 84px rgba(116,75,218,.02);
    pointer-events: none;
  }

  .layout-content {
    width: 1200px;
    max-width: calc(100vw - 48px);
  }

  @include mobile {
    padding-bottom: 30px;

    .layout-content {
      width: 90vw;
      max-width: none;
    }
  }
}

.tab-box {
  margin-top: 22px;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 5px;
  width: 100%;
  padding: 5px;
  border: 1px solid rgba(38,57,102,.05);
  border-radius: 12px;
  background: #f0f3f8;
}

.region-switch {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  width: 640px;
  max-width: 100%;
  margin: -12px auto 36px;
  padding: 5px;
  border: 1px solid #e2e6eb;
  border-radius: 15px;
  background: #e9edf4;
}

.region-option {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 62px;
  padding: 10px 18px;
  border: 0;
  border-radius: 11px;
  color: #8a9098;
  text-align: left;
  background: transparent;
  outline: none;
  cursor: pointer;
  transition: color .2s ease, background .2s ease, box-shadow .2s ease;

  strong {
    font-size: 16px;
    font-weight: 600;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  small {
    font-size: 9px;
    letter-spacing: 1px;
  }

  &.active {
    color: #1f242b;
    background: #fff;
    box-shadow: 0 5px 16px rgba(27, 38, 54, .08);

    &::after { content: ''; position: absolute; left: 18px; top: 50%; width: 3px; height: 24px; border-radius: 99px; background: var(--track-blue); transform: translateY(-50%); }
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px rgba(211, 70, 76, .24);
  }
}

.btn-tab {
  border: none;
  flex: 1;
  min-width: 0;
  height: 40px;
  line-height: 40px;
  background-image: none;
  padding: 0 8px;
  border-radius: 8px;
  font-size: 12px;
  color: #737d8e;
  text-align: center;
  background-color: transparent;
  font-family: "Alibaba PuHuiTi 3.0";
  cursor: pointer;
  font-weight: 300;
  transition: all .15s ease-in-out;
  white-space: normal;
  overflow-wrap: anywhere;
  position: relative;
  &.active {
    color: var(--track-pink);
    font-weight: 600;
    background-color: #FFFFFF;
    box-shadow: 0 2px 8px rgba(32, 44, 63, 0.08);
    &::after {
      content: "";
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 22px;
      height: 2px;
      border-radius: 99px;
      background-color: var(--track-pink);
    }
  }
  &:hover {
    color: #000;
    font-weight: normal;
  }
}

.l-content {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: stretch;
  gap: 24px;
  position: relative;

  &.is-international {
    grid-template-columns: minmax(0, 760px);
    justify-content: center;

    .block::before {
      background: linear-gradient(90deg, #6259d8, var(--track-cyan));
    }

    .track-index,
    .word.is-origin {
      color: var(--track-cyan);
    }
  }

  @include mobile {
    grid-template-columns: 1fr;
    gap: 0;
    margin-top: 6px;
  }
}

.track-panel-enter-active,
.track-panel-leave-active {
  transition: opacity .18s ease, transform .18s ease;
}

.track-panel-enter,
.track-panel-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
.intro-subintro{
  color: #000;
}
.block {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  width: auto;
  height: 100%;
  box-sizing: border-box;
  padding: 28px 30px 26px;
  background: #FFFFFF;
  border: 1px solid #E7EBEF;
  border-radius: 20px;
  box-shadow: 0 10px 28px rgba(30,44,74,.045);
  overflow: hidden;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #7657d5, var(--track-blue));
  }

  &.block--blockchain::before {
    background: linear-gradient(90deg, #9b55ba, var(--track-pink));
  }

  &:hover {
    border-color: rgba(49,95,216,.2);
    box-shadow: 0 14px 32px rgba(30,44,74,.07);
  }

  @include mobile {
    width: 100%;
    height: auto;
    min-height: 0;
    padding: 24px 20px 22px;
    margin-bottom: 20px;
    border-radius: 16px;

    &:hover {
      transform: none;
    }
  }
}

@include mobile {
  .region-switch {
    margin: -8px auto 26px;
    padding: 4px;
  }

  .region-option {
    display: flex;
    justify-content: center;
    min-height: 52px;
    padding: 8px 10px;
    text-align: center;

    small { display: none; }

    strong { font-size: 14px; }

    &.active::after {
      left: 12px;
      right: 12px;
    }
  }
}

.title {
  display: flex; align-items: center;
  width: 100%;
}

.track-identity {
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) 32px;
  align-items: center;
  gap: 9px;
  min-height: 34px;
  margin-bottom: 15px;
  padding-bottom: 11px;
  border-bottom: 1px solid rgba(35,53,92,.08);
}

.track-index {
  width: 28px;
  color: currentColor;
  font-family: DIN-BlackItalic, DIN;
  font-size: 13px;
}

.track-name {
  min-width: 0;
  color: #17213a;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.3;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.track-kicker {
  color: #8a93a3;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: .12em;
}

.word {
  display: flex; flex-direction: column;
  width: 100%;
  min-width: 0;
}

.word-img {
  width: 32px; height: 32px;
  margin-bottom: 0;
  object-fit: contain;

  @include mobile {
    width: 34px; height: 34px;
  }
}

.word {
  &.is-purple {
    color: #7550E1;
  }

  &.is-blue {
    color: #376EFD;
  }

  &.is-origin {
    color: #E1574D;
  }
}

.word-title {
  color: #18233a;
  font-size: 19px; font-weight: 650;
  line-height: 1.35;
  overflow-wrap: anywhere;

  @include mobile {
    margin-bottom: 0;
    font-size: 18px; font-weight: 650;
    line-height: 1.45;
  }
}

.mt88 {
  margin-top: 88px;
}

.mt24{
  margin-top: 18px;
}

.intro-title {
  min-height: 0;
  color: #202a3e;
  font-size: 17px; font-weight: 650;
  line-height: 1.5;

  @include mobile {
    min-height: 0;
    font-size: 18px; font-weight: 600;
    line-height: 30px;
  }
}

.ai-requirements {
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid rgba(49, 95, 216, .14);
}

.ai-requirements-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;

  span {
    color: #315fd8;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: .08em;
  }

  strong {
    color: #dc3e73;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
  }
}

.ai-scenarios {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.ai-scenario {
  appearance: none;
  font-family: inherit;
  text-align: left;
  display: grid;
  grid-template-columns: 26px minmax(0, 1fr);
  gap: 9px;
  min-width: 0;
  min-height: 92px;
  padding: 12px 10px;
  border: 1px solid #e7ebf2;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  transition: border-color .18s ease, background .18s ease, box-shadow .18s ease, transform .18s ease;

  &:hover {
    border-color: rgba(49, 95, 216, .34);
    background: #f8faff;
  }

  &.is-selected {
    border-color: #315fd8;
    background: #f7f9ff;
    box-shadow: inset 3px 0 0 #315fd8;

    .ai-scenario-index {
      color: #dc3e73;
    }
  }

  > div { min-width: 0; }
}

.ai-scenario-index {
  color: #315fd8;
  font-family: DIN-BlackItalic, DIN;
  font-size: 12px;
  line-height: 18px;
}

.ai-scenario strong {
  display: block;
  color: #202a3e;
  font-size: 12px;
  line-height: 18px;
}

.ai-scenario p {
  margin-top: 3px;
  color: #6e7889;
  font-size: 11px;
  line-height: 17px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}

.international-directions {
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid rgba(4, 169, 189, .18);
}

.direction-list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.direction-card {
  appearance: none;
  display: grid;
  grid-template-columns: 27px minmax(0, 1fr);
  gap: 9px;
  min-width: 0;
  min-height: 104px;
  padding: 12px 10px;
  border: 1px solid #dcebef;
  border-radius: 8px;
  color: inherit;
  font-family: inherit;
  text-align: left;
  background: #fff;
  cursor: pointer;
  transition: border-color .18s ease, background .18s ease, box-shadow .18s ease, transform .18s ease;

  &:hover {
    border-color: rgba(4, 169, 189, .5);
    background: #f5fbfc;
  }

  &.is-selected {
    border-color: #079db9;
    background: #f3fbfc;
    box-shadow: inset 3px 0 0 #079db9;
  }
}

.direction-index {
  color: #078da9;
  font-family: DIN-BlackItalic, DIN;
  font-size: 16px;
  line-height: 20px;
}

.direction-card strong {
  display: block;
  color: #202a3e;
  font-size: 12px;
  line-height: 18px;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.direction-card p {
  margin-top: 3px;
  color: #6e7889;
  font-size: 11px;
  line-height: 17px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}

@include mobile {
  .ai-requirements-head {
    display: block;

    strong {
      display: block;
      margin-top: 5px;
      white-space: normal;
    }
  }

  .ai-scenarios {
    grid-template-columns: 1fr;
  }

  .direction-list {
    grid-template-columns: 1fr;
  }

}

.mt25{
  margin-top: 25px;
}

.btn {
  width: 100px;
  color: $black;
  font-size: 16px;
  text-align: center;
  opacity: 0.5;
  border: 1px solid #828991;
}

.wrapper {
  position: absolute; top: 100%; right: -2px; z-index: 100;
  width: 250px;
}

.word-text-icon {
  display: inline-block;
  width: 16px; height: 16px;
  margin: 0 5px;
  vertical-align: middle;
  background: url('~@/assets/transSave/resource/image/c0/df/c07e016074c5102d9e41d8yy84203fdf.png') center / cover no-repeat;
}

.word-text {
  color: #303030;
  line-height: 20px;
  font-size: 14px; font-weight: 400;
}

.intro {
  margin: 10px 0 0;
  padding-bottom: 0;
  color: #5c6678;
  line-height: 1.72;
  font-size: 13px; font-weight: 400;
  opacity: 1;
  overflow-wrap: anywhere;
  word-break: break-word;

  .block > & {
    flex: 0 0 auto;
  }

  @include mobile {
    height: auto;
    margin: 14px 0 20px;
    font-size: 14px; font-weight: 400;
    color: #000000;
    line-height: 26px;
  }

  ::v-deep .data-source-highlight {
    padding: 0 2px;
    border-radius: 3px;
    color: #b72f61;
    font-weight: 700;
    text-decoration-line: underline;
    text-decoration-color: rgba(220, 62, 115, .45);
    text-decoration-thickness: 1px;
    text-underline-offset: 3px;
    background: linear-gradient(180deg, transparent 54%, rgba(220, 62, 115, .13) 54%);
    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
    cursor: pointer;
    transition: color .2s ease, background-color .2s ease, text-decoration-color .2s ease;

    &:hover {
      color: #9f1f4f;
      text-decoration-color: currentColor;
      background-color: rgba(220, 62, 115, .08);
    }

    &:focus-visible {
      outline: 2px solid #315fd8;
      outline-offset: 2px;
    }
  }
}

.data-download {
  margin-top: 14px;
}

.data-download-link {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  min-height: 42px;
  max-width: 100%;
  padding: 8px 12px 8px 14px;
  box-sizing: border-box;
  border: 1px solid rgba(220, 62, 115, .28);
  border-radius: 10px;
  color: #b72f61;
  background: rgba(220, 62, 115, .055);
  text-decoration: none;
  cursor: pointer;
  transition: color .2s ease, border-color .2s ease, background-color .2s ease, box-shadow .2s ease;

  &:hover {
    color: #a32353;
    border-color: rgba(220, 62, 115, .55);
    background: rgba(220, 62, 115, .09);
    box-shadow: 0 5px 14px rgba(139, 42, 78, .08);
  }

  &:focus-visible {
    outline: 2px solid #315fd8;
    outline-offset: 3px;
  }
}

.data-download-copy {
  display: flex;
  align-items: baseline;
  min-width: 0;
  gap: 8px;

  strong {
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
  }

  small {
    color: #777f8d;
    font-size: 10px;
    line-height: 1.4;
  }
}

.data-download-icon {
  flex: 0 0 18px;
  width: 18px;
  height: 18px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

@include mobile {
  .data-download {
    margin-top: -6px;
    margin-bottom: 18px;
  }

  .data-download-link {
    display: flex;
    width: 100%;
    min-height: 48px;
    padding: 9px 12px 9px 14px;
  }

  .data-download-copy {
    flex-wrap: wrap;
    gap: 1px 8px;

    strong {
      font-size: 13px;
    }

    small {
      font-size: 11px;
    }
  }
}

.block-btn {
  position: static;
  display: flex;
  margin-top: auto;
  padding-top: 18px;
}

.block-btn-link {
  &:hover .block-btn-text {
    color: #000000;
    opacity: 0.7;
  }
}

.block-btn-default {
    min-width: 132px;
    height: 38px;
    margin-right: 20px;
    padding: 0 18px;
    text-align: center;
    font-size: 12px;
    font-weight: 500;
    border: 1px solid #000000;
    line-height: 38px;
    color: var(--track-blue);
    border-color: rgba(49,95,216,.55);
    border-radius: 9px;
    opacity: 0.9;
    cursor: pointer;

  @include mobile {
    height: 34px;
        line-height: 34px;
        padding: 0 15px;
  }

  &:hover {
    background: var(--track-blue);
    color: #FFFFFF;
    opacity: 0.9;

    @include mobile {
      background: none;
      color: #000000;
      opacity: 1;
    }
  }
}

</style>
